import { logError } from 'tradera/utils/api';
import { webApiClient } from 'tradera/utils/http';
import { useCurrentLanguage } from 'tradera/lang/translate';
import { useSelector } from 'react-redux';
import { selectAutomaticTranslationPreferred } from 'tradera/state/language/selectors';
import { useCallback } from 'react';

export const useGetSuggestions = () => {
    const automaticTranslationPreferred = useSelector(
        selectAutomaticTranslationPreferred,
    );
    const { language } = useCurrentLanguage();
    return useCallback(
        (params) =>
            getSuggestions({
                ...params,
                automaticTranslationPreferred,
                languageCodeIso2: language,
            }),
        [automaticTranslationPreferred, language],
    );
};

const getSuggestions = async ({
    searchTerm = '',
    limitPrevious = 7,
    enablePrevious = false,
    enableSuggestions = false,
    recentSearches = [],
    automaticTranslationPreferred,
    languageCodeIso2,
    selectedCategoryId,
}) => {
    const generateSuggestionId = (categoryId, term) =>
        categoryId ? categoryId + '-' + term : term;

    let suggestedResults = {
        searches: [],
        shops: [],
        charities: [],
        campaigns: [],
    };

    let previousSearchResults = [];

    // ------------------------------
    // get suggestion data
    if (enablePrevious) {
        //Filter based on search term
        const filteredRecentSearches =
            searchTerm.length === 0
                ? recentSearches
                : recentSearches.filter((search) =>
                      search.text.includes(searchTerm?.toLowerCase()),
                  );
        previousSearchResults = filteredRecentSearches?.slice(0, limitPrevious);
    }

    if (enableSuggestions) {
        suggestedResults = await getSuggestionsFromBackend(
            searchTerm,
            automaticTranslationPreferred,
            languageCodeIso2,
            selectedCategoryId,
        );
    }

    // ------------------------------
    // merge lists
    const withDuplicates = [].concat(
        previousSearchResults.map((item, index) => ({
            key: `previous_search-${index}`,
            type: 'previous',
            text: item.text,
            queryParams: item.queryParams,
            id: generateSuggestionId(null, index),
        })),
        suggestedResults.searches.map((item, index) => ({
            key: `suggested_search-${index}`,
            type: 'suggested-item',
            categories: item.categories,
            item,
            text: item.text,
            id: generateSuggestionId(
                item.category ? item.category.id : null,
                item.text,
            ),
        })),
    );
    const uniqueMap = new Map();
    withDuplicates.forEach((item) => {
        if (uniqueMap.has(item.id)) return;
        uniqueMap.set(item.id, item);
    });
    const uniqueResults = Array.from(uniqueMap.values());

    const shopResults = suggestedResults.shops.map((item, key) => ({
        key: `suggested_shop-${key}`,
        type: 'suggested-shop',
        item,
        memberId: item.memberId,
        name: item.name,
        urlName: item.urlName,
        inSPA: false, // Turned off until profile pages are SPAified
        id: key,
    }));

    const suggestedCharitiesResults = suggestedResults.charities || [];
    const charitiesResults = suggestedCharitiesResults.map((item, key) => ({
        key: `suggested_charity-${key}`,
        type: 'suggested-charity',
        item,
        name: item.name,
        slug: item.slug,
        inSPA: false, // Turned off until profile pages are SPAified
        id: key,
    }));

    const suggestedCampaignResults = suggestedResults.campaigns || [];
    const campaignResults = suggestedCampaignResults.map((item, key) => ({
        key: `suggested_campaign-${key}`,
        type: 'suggested-campaign',
        item,
        capaignId: item.campaignId,
        name: item.name,
        urlName: item.urlName,
        inSPA: false, // Turned off until profile pages are SPAified
        id: key,
    }));

    const allSuggestions = [
        ...campaignResults,
        ...uniqueResults,
        ...shopResults,
        ...charitiesResults,
        {
            key: 'seller-search',
            type: 'link-seller-search',
        },
        {
            key: 'advanced-search',
            type: 'link-advanced-search',
        },
    ];
    return allSuggestions;
};

// --------------------------------------------
// backend suggestions
// --------------------------------------------

const normalizeSearches = (searches = []) => {
    return searches.reduce((acc, search) => {
        if (search.categories) {
            search.categories.map((category) => {
                acc.push({
                    text: search.text,
                    category: category,
                });
            });
        } else {
            acc.push({
                text: search.text,
                category: null,
            });
        }
        return acc;
    }, []);
};

const getSuggestionsFromBackend = async (
    query,
    automaticTranslationPreferred,
    languageCodeIso2,
    selectedCategoryId,
) => {
    try {
        const response = await webApiClient.get('/browse/suggestions', {
            params: {
                query,
                automaticTranslationPreferred,
                languageCodeIso2,
                selectedCategoryId,
            },
        });

        const {
            campaigns = [],
            searches,
            shops = [],
            charities = [],
        } = response.data;
        return {
            campaigns,
            searches: normalizeSearches(searches) || [],
            shops,
            charities,
        };
    } catch (error) {
        logError(error);
        return { searches: [], shops: [] };
    }
};
